import * as React from 'react';
import {
    TournamentConstraints,
    TournamentOperator,
} from '../../../../generated/protocol';
import { Condition } from '../../form/Condition';
import { FormComponentProps, KeyValue } from '../../form/Form';
import { FormGroup } from '../../form/FormGroup';
import { FormInput } from '../../form/FormInput';
import { FormTextArea } from '../../form/FormTextArea';
import { FormTransferContainer } from '../../form/FormTransferContainer';
import { SelectInput } from '../../form/SelectInput';
import { Validators } from '../../form/Validators';
import { PokerGameSettingsPanel } from './PokerGameSettingsPanel';
import { RebuyConfigPanel } from './RebuyConfigPanel';
import { TournamentInfoPanel } from './TournamentInfoPanel';
import { PrizeBoostConfigurationPanel } from './PrizeBoostConfigurationPanel';
import { BountyTournamentPanel } from './BountyTournamentPanel';
import {FeatureToggleService} from "../../../service/FeatureToggleService";
import {TimedTournamentPanel} from "./TimedTournamentPanel";

interface TournamentConfigPanelProps extends FormComponentProps {
    constraints?: TournamentConstraints;
    sitAndGo?: boolean;
    isTarget?: boolean;
}

interface TournamentConfigPanelState extends FormComponentProps {
    rebuyEnabled?: boolean;
}

const CONFIGURATION_MODEL = 'configuration';
const CONFIGURATION_REBUY_MODEL = 'configuration.rebuyConfiguration';

export class TournamentConfigPanel extends React.Component<TournamentConfigPanelProps, TournamentConfigPanelState> {
    state = {
        rebuyEnabled: this.rebuyExists()
    };

    getModel() {
        return this.props.fieldSet.getModelValue(CONFIGURATION_MODEL);
    }

    isDisabledCurrency(): boolean {
        const configuration = this.getModel();
        return configuration.dummyTournament || configuration.satelliteToDummy;
    }

    isPrivate(): boolean  {
        const configuration = this.getModel();
        return configuration.passwordEnabled;
    }

    rebuyExists(): boolean {
        const configuration = this.getModel();
        return configuration.rebuyConfiguration.numberOfRebuysAllowed !== 0;
    }

    updateRebuy = () => {
        const updatedRebuyEnabled = !this.state.rebuyEnabled;
        this.setState({rebuyEnabled : updatedRebuyEnabled});
        if (updatedRebuyEnabled) {
            this.props.fieldSet.updateModels([
                new KeyValue<any>(CONFIGURATION_REBUY_MODEL, {}, true, {errorName: null, pristine: true}),
            ]);
        } else {
            this.props.fieldSet.updateModels([
                new KeyValue<any>(CONFIGURATION_REBUY_MODEL, undefined, true, {errorName: null, pristine: true}),
            ]);
        }
    }

    render() {
        const configuration = this.getModel();
        const constraints: TournamentConstraints = this.props.constraints;
        const disabledCurrency = this.isDisabledCurrency();
        const guaranteedPrizePool = this.props.constraints.guaranteedPrizePool;
        const isBounty = configuration.hasBounty;
        const canHaveLateReg = !(configuration.isRandomizer || configuration.isShootout || this.props.sitAndGo);
        const canHaveRebuys = !(configuration.isRandomizer || configuration.isShootout || isBounty);

        return (
            <FormTransferContainer {...this.props}>
                <TournamentInfoPanel/>
                <PokerGameSettingsPanel sitAndGo={this.props.sitAndGo} canHaveLateReg={canHaveLateReg} constraints={this.props.constraints}/>

                <div className='row'>
                    <div className='col-lg-12'>
                        <div className='ibox float-e-margins'>
                            <div className='ibox-title'>
                                <h5>Buy-in</h5>
                            </div>
                            <div className='ibox-content'>
                                <FormGroup className='form-group'>
                                    <label className='col-sm-2 control-label'>Currency:</label>
                                    <div className='col-sm-10'>
                                        <SelectInput disabled={disabledCurrency} model='configuration.currency'
                                                     className='form-control'
                                                     values={constraints.currencyCodes}/>
                                    </div>
                                </FormGroup>

                                <div className='hr-line-dashed'></div>

                                <FormGroup className='form-group'>
                                    <label className='col-sm-2 control-label'>Buy-in</label>
                                    <div className='col-sm-10'>
                                        <FormInput type='float' model='configuration.buyIn' className='form-control'
                                                   validators={Validators.bigDecimalConstraint(constraints.buyIn)}/>
                                    </div>
                                </FormGroup>

                                <div className='hr-line-dashed'></div>

                                <FormGroup className='form-group'>
                                    <label className='col-sm-2 control-label'>Fee</label>
                                    <div className='col-sm-10'>
                                        <FormInput type='text' model='configuration.fee' className='form-control'
                                                   validators={Validators.bigDecimalConstraint(constraints.fee)}/>
                                    </div>
                                </FormGroup>

                                <div className='hr-line-dashed'></div>

                            </div>
                        </div>
                    </div>
                </div>

                {this.getTimedTournamentPanel()}
                <BountyTournamentPanel hasRebuy={this.state.rebuyEnabled} payoutStructures={this.props.constraints.payoutStructures}/>

                <div className='row'>
                    <div className='col-lg-12'>
                        <div className='ibox float-e-margins'>
                            <div className='ibox-title'>
                                <h5>Payout</h5>
                            </div>
                            <div className='ibox-content'>
                                <FormGroup className='form-group'>
                                    <label className='col-sm-2 control-label'>Payout Structure</label>
                                    <div className='col-sm-10'>
                                        <SelectInput
                                            className='form-control m-b'
                                            model='configuration.payoutStructureId'
                                            values={this.props.constraints.payoutStructures}
                                            displayValue={a => a.name}
                                            optionModelValue={a => a.id}
                                        />
                                    </div>
                                </FormGroup>

                                <div className='hr-line-dashed'></div>

                                <FormGroup className='form-group'>
                                    <label className='col-sm-2 control-label'>Guaranteed Prize Pool</label>
                                    <div className='col-sm-10'>
                                        <FormInput type='number' model='configuration.guaranteedPrizePool'
                                                   validators={Validators.bigDecimalConstraint(guaranteedPrizePool)}
                                                   className='form-control'/>
                                    </div>
                                </FormGroup>

                                <div className='hr-line-dashed'></div>
                            </div>
                            {!this.props.sitAndGo && <PrizeBoostConfigurationPanel/>}
                        </div>
                    </div>
                </div>

                <div className='row'>
                    <div className='col-lg-12'>
                        <div className='ibox float-e-margins'>
                            <div className='ibox-title'>
                                <h5>Restrict User Access</h5>
                            </div>
                            <div className='ibox-content'>

                                <FormGroup className='form-group'>
                                    <label className='col-sm-2 control-label'>Achievements required</label>
                                    <div className='col-sm-10'>
                                        <FormInput model='configuration.achievementsRequired' type='text'
                                                   className='form-control'/>
                                        <span className='help-block m-b-none'>
                                            Comma-separated list of achieved achievement name-id's required
                                            to be able to register to this tournament.
                                        </span>
                                    </div>
                                </FormGroup>
                                <div className='hr-line-dashed'></div>
                                <FormGroup className='form-group'>
                                    <label className='col-sm-2 control-label'>Access Description</label>
                                    <div className='col-sm-10'>
                                        <FormTextArea model='configuration.userRuleExpressionDescription'
                                                      className='form-control'/>
                                        <span className='help-block m-b-none'>
                                            Description for achievements required.
                                            Displayed when player hovers above tournament lock icon in lobby.
                                        </span>
                                    </div>
                                </FormGroup>
                                <div className='hr-line-dashed'></div>
                                <FormGroup className='form-group'>
                                    <label className='col-sm-2 control-label'>Block IP-collisions</label>
                                    <div className='col-sm-10'>
                                        <FormInput model='configuration.ipBlock' type='checkbox'/>
                                        <span className='help-block m-b-none'>
                                            If enabled then players with the same IP address
                                            will not be able to register for this tournament
                                        </span>
                                    </div>
                                </FormGroup>
                                {!this.props.isTarget &&
                                <div>
                                    <div className='hr-line-dashed'/>
                                    <FormGroup className='form-group'>
                                        <label className='col-sm-2 control-label'>Enable Password</label>
                                        <div className='col-sm-10'>
                                            <FormInput model='configuration.passwordEnabled' type='checkbox'/>
                                            <span className='help-block m-b-none'>
                                                If enabled this tournament will require players to submit a password on registration.
                                            </span>
                                        </div>
                                    </FormGroup>
                                </div>}
                                {this.isPrivate() && <FormGroup className='form-group'>
                                    <label className='col-sm-2 control-label'>Enter Password</label>
                                    <div className='col-sm-10'>
                                        <FormInput model='configuration.password' className='form-control' maxLength={32} />
                                        <span className='help-block m-b-none'>
                                            The password required to enter the tournament.
                                        </span>
                                    </div>
                                </FormGroup>}
                                <div className='hr-line-dashed'/>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='row'>
                    <div className='col-lg-12'>
                        <div className='ibox float-e-margins'>
                            <div className='ibox-title'>
                                <h5>Rebuy And Add-on Settings</h5>
                            </div>
                            <div className='ibox-content'>
                                <div className='form-group'>
                                    <label className='col-sm-2 control-label'>Enable Rebuy And Add-ons</label>
                                    <div className='col-sm-10'>
                                        <input type='checkbox' checked={this.state.rebuyEnabled} onChange={this.updateRebuy} disabled={!canHaveRebuys}/>
                                    </div>
                                </div>
                                <div className='hr-line-dashed'></div>
                                {this.state.rebuyEnabled && <RebuyConfigPanel/>}
                            </div>
                        </div>
                    </div>
                </div>

                <Condition show={constraints.useBroadcast}>
                    <div className='row'>
                        <div className='col-lg-12'>
                            <div className='ibox float-e-margins'>
                                <div className='ibox-title'>
                                    <h5>Broadcasting</h5>
                                </div>
                                <div className='ibox-content'>
                                    <FormGroup className='form-group'>
                                        <label className='col-sm-2 control-label'>Enable Broadcasting</label>
                                        <div className='col-sm-10'>
                                            <FormInput model='configuration.broadcastEnabled' type='checkbox'/>
                                        </div>
                                    </FormGroup>
                                    <div className='hr-line-dashed'></div>
                                    <FormGroup className='form-group'>
                                        <label className='col-sm-2 control-label'>Enable Rabbit Hunting</label>
                                        <div className='col-sm-10'>
                                            <FormInput model='configuration.rabbitHunting' type='checkbox'/>
                                        </div>
                                    </FormGroup>
                                    <div className='hr-line-dashed'></div>
                                    <div className='form-group'>
                                        <label className='col-sm-2 control-label'>Enable Win percentages</label>
                                        <div className='col-sm-10'>
                                            <FormInput model='configuration.showWinPercentage' type='checkbox'/>
                                        </div>
                                    </div>
                                    <div className='hr-line-dashed'></div>
                                    <div className='form-group'>
                                        <label className='col-sm-2 control-label'>Enable Pause Game</label>
                                        <div className='col-sm-10'>
                                            <FormInput model='configuration.pauseEnabled' type='checkbox'/>
                                        </div>
                                    </div>
                                    <div className='hr-line-dashed'></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Condition>

            </FormTransferContainer>
        );
    }

    private getTimedTournamentPanel = () => {
        if(!FeatureToggleService.timedTournamentFeatureFlag.isEnabled) return;
        if(!this.props.sitAndGo) return;
        return <TimedTournamentPanel/>
    }
}



// WEBPACK FOOTER //
// ./src/app/ui/poker/tournament/TournamentConfigPanel.tsx